import * as THREE from 'three';
import { LODStaticObject, StaticObjectType } from './LODStaticObject';
import { BoundingBox } from '../../physics/BoundingVolume';

/**
 * Low polygon loaf represented as a stack of toast slices with flat shading
 */
export class LODLoaf extends LODStaticObject {
  /** Total height of the toast stack for bounding volume calculation */
  private readonly stackHeight: number = 5.0; // 10 slices × 0.5 height
  
  /** Scale of the loaf object */
  private readonly loafScale: THREE.Vector3;

  constructor(name: string = 'LODLoaf', scale: THREE.Vector3 = new THREE.Vector3(1, 1, 1)) {
    // Store the scale for bounding volume calculation
    const tempScale = new THREE.Vector3().copy(scale);
    
    // Create toast materials with flat shading
    const crustMaterial = new THREE.MeshStandardMaterial({
      color: 0xD2691E, // Brown color for crust
      flatShading: true,
      roughness: 0.9,
      metalness: 0.1
    });
    
    const interiorMaterial = new THREE.MeshStandardMaterial({
      color: 0xFFF8DC, // Cornsilk color for bread interior
      flatShading: true,
      roughness: 0.7,
      metalness: 0.0
    });
    
    // Create with loaf-specific configuration
    super({
      type: StaticObjectType.LOAF,
      name: name,
      baseScale: tempScale,
      materialOverrides: {
        high: crustMaterial,
        medium: crustMaterial,
        low: interiorMaterial
      }
    });
    
    // Store scale after super call
    this.loafScale = new THREE.Vector3().copy(scale);
    
    // Create a bounding volume for the entire toast stack
    this.createBoundingVolume();
  }

  /**
   * Create a bounding volume for collision detection
   * The box encompasses the entire stack of toast slices
   */
  private createBoundingVolume(): void {
    // Calculate half extents of the stack based on slice dimensions
    const halfWidth = 1.1 * this.loafScale.x;  // Slightly larger than the widest slice
    const halfHeight = (this.stackHeight / 2) * this.loafScale.y;
    const halfDepth = 1.1 * this.loafScale.z;  // Slightly larger than the deepest slice
    
    // Create half extents vector for bounding box
    const halfExtents = new THREE.Vector3(halfWidth, halfHeight, halfDepth);
    
    // Create bounding box and set it
    const boundingBox = new BoundingBox(halfExtents, this.getPosition());
    this.setBoundingVolume(boundingBox);
  }
} 